import { memo, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useAuth from 'hooks/useAuth';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useTopPanel from 'hooks/useTopPanel';
import ObjectBlockMultiFilter from 'components/ui/filters/items/ObjectBlockMultiFilter';

// -----------------------------------------------------------------------------
// Helper cards
// -----------------------------------------------------------------------------

const Money = ({ value, suffix }) => (
  <NumericFormat
    value={value}
    displayType='text'
    thousandSeparator=' '
    decimalScale={0}
    allowNegative={false}
    suffix={suffix}
    className='font-bold'
  />
);

const StatusCard = ({ name, count, color, icon }) => (
  <div
    className='rounded-xl bg-white p-4 shadow-sm border-l-4 flex items-center gap-3'
    style={{ borderLeftColor: color }}
  >
    <div
      className='w-10 h-10 rounded-lg flex items-center justify-center text-lg'
      style={{ background: `${color}1a`, color: color }}
    >
      <i className={icon} />
    </div>
    <div className='flex-1 min-w-0'>
      <div className='text-xs text-gray-500 truncate'>{name}</div>
      <div className='text-xl font-bold text-gray-800'>{count}</div>
    </div>
  </div>
);

/**
 * Mijozlar haqdorligi (qarzdorlik) - ikkiga bo'lingan karta.
 * Muddati o'tgan (qizil) + Muddati kelmagan (sariq).
 */
const DebtRow = ({ color, label, icon, sum, valute }) => (
  <div
    className='flex flex-col gap-0.5 p-2 rounded-lg'
    style={{ background: `${color}10` }}
  >
    <div
      className='flex items-center gap-1.5 text-xs font-medium'
      style={{ color }}
    >
      <i className={icon} />
      {label}
    </div>
    <div className='flex flex-col leading-tight pl-5'>
      {sum > 0 ? (
        <div className='text-sm text-gray-800'>
          <Money value={sum} suffix=' UZS' />
        </div>
      ) : null}
      {valute > 0 ? (
        <div className='text-sm text-gray-800'>
          <Money value={valute} suffix=' $' />
        </div>
      ) : null}
      {!sum && !valute ? (
        <div className='text-sm text-gray-400'>0</div>
      ) : null}
    </div>
  </div>
);

const DebtCard = ({
  title,
  overdueLabel,
  upcomingLabel,
  overdue,
  upcoming,
}) => (
  <div className='rounded-xl bg-white p-4 shadow-sm border base-border flex flex-col gap-2'>
    <div className='flex items-center gap-2'>
      <div
        className='w-9 h-9 rounded-lg flex items-center justify-center text-base'
        style={{ background: '#f59e0b1a', color: '#f59e0b' }}
      >
        <i className='bi bi-exclamation-circle' />
      </div>
      <div className='text-sm font-medium text-gray-600 flex-1'>{title}</div>
    </div>
    <div className='flex flex-col gap-2 mt-1'>
      <DebtRow
        color='#ef4444'
        icon='bi bi-clock-history'
        label={overdueLabel}
        sum={overdue?.sum || 0}
        valute={overdue?.valute || 0}
      />
      <DebtRow
        color='#f59e0b'
        icon='bi bi-hourglass-split'
        label={upcomingLabel}
        sum={upcoming?.sum || 0}
        valute={upcoming?.valute || 0}
      />
    </div>
  </div>
);

const KpiCard = ({ title, icon, color, sum, valute, footer }) => (
  <div className='rounded-xl bg-white p-4 shadow-sm border base-border flex flex-col gap-2'>
    <div className='flex items-center gap-2'>
      <div
        className='w-9 h-9 rounded-lg flex items-center justify-center text-base'
        style={{ background: `${color}1a`, color: color }}
      >
        <i className={icon} />
      </div>
      <div className='text-sm font-medium text-gray-600 flex-1'>{title}</div>
    </div>
    <div className='flex flex-col gap-1 mt-1'>
      {sum > 0 ? (
        <div className='text-lg text-gray-800'>
          <Money value={sum} suffix=' UZS' />
        </div>
      ) : null}
      {valute > 0 ? (
        <div className='text-lg text-gray-800'>
          <Money value={valute} suffix=' $' />
        </div>
      ) : null}
      {!sum && !valute ? (
        <div className='text-lg text-gray-400'>0</div>
      ) : null}
    </div>
    {footer ? (
      <div className='text-xs text-gray-500 pt-1 border-t mt-1'>{footer}</div>
    ) : null}
  </div>
);

// Home::list ids: 1=ACTIVE(Bo'sh), 2=TIME(Band), 3=ORDERED(Sotilgan), 4=DEACTIVE(Aktiv emas)
const STATUS_META = {
  1: { color: '#3b82f6', icon: 'bi bi-house' },         // Bo'sh – blue
  2: { color: '#f59e0b', icon: 'bi bi-house-lock' },    // Band  – amber
  3: { color: '#22c55e', icon: 'bi bi-house-check' },   // Sotilgan – green
  4: { color: '#ef4444', icon: 'bi bi-house-x' },       // Aktiv emas – red
};

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------

const Dashboard = () => {
  const { setComponent } = useTopPanel();
  const { t, i18n } = useTranslation();
  const [{ user }] = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const { search } = useLocation();

  useEffect(() => {
    setComponent(
      <div className='component-title'>{t('boss.dashboard.title')}</div>
    );
  }, [i18n.language]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['boss-dashboard', search],
    queryFn: async () => {
      const response = await axiosPrivate.get(
        `/analytic/boss-dashboard/index${search || ''}`
      );
      return response.data.data;
    },
    retry: false,
  });

  const loading = isLoading || isFetching;

  const statuses = data?.home_statuses?.statuses || [];
  const total = data?.home_statuses?.total || 0;

  return (
    <div className='component-wrapper boss-dashboard-wrapper'>
      {/* Header */}
      <div className='dashboard-actions flex items-center justify-between gap-4 mb-4'>
        <div className='dashboard-owner-greeting'>
          {t('boss.dashboard.welcome')},{' '}
          <span className='text-xl font-medium'>{user?.user?.name}</span>
        </div>
        <div className='max-w-[300px] w-full'>
          <ObjectBlockMultiFilter />
        </div>
      </div>

      {loading ? (
        <div className='flex justify-center py-12'>
          <CircularProgress size={36} />
        </div>
      ) : (
        <div className='flex flex-col gap-6'>
          {/* 1. Uy statuslari */}
          <section>
            <div className='text-base font-semibold mb-3 flex items-center gap-2'>
              <i className='bi bi-building text-blue-500' />
              {t('boss.dashboardV2.homeStatuses')}
            </div>
            <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3'>
              {statuses.map((s) => (
                <StatusCard
                  key={s.id}
                  name={s.name}
                  count={s.count}
                  color={STATUS_META[s.id]?.color || '#6b7280'}
                  icon={STATUS_META[s.id]?.icon || 'bi bi-house'}
                />
              ))}
              <StatusCard
                name={t('boss.dashboardV2.total')}
                count={total}
                color='#6366f1'
                icon='bi bi-grid-3x3-gap-fill'
              />
            </div>
          </section>

          {/* 2-5. Asosiy KPI'lar */}
          <section>
            <div className='text-base font-semibold mb-3 flex items-center gap-2'>
              <i className='bi bi-graph-up text-green-500' />
              {t('boss.dashboardV2.financialKpi')}
            </div>
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
              {/* 2. Undirilgan mablag' */}
              <KpiCard
                title={t('boss.dashboardV2.collected')}
                icon='bi bi-cash-coin'
                color='#22c55e'
                sum={data?.collected?.sum || 0}
                valute={data?.collected?.valute || 0}
              />

              {/* 3. Mijozlardagi haqdorlik - muddati o'tgan va kelmagan */}
              <DebtCard
                title={t('boss.dashboardV2.outstanding')}
                overdueLabel={t('boss.dashboardV2.overdue')}
                upcomingLabel={t('boss.dashboardV2.upcoming')}
                overdue={data?.outstanding?.overdue}
                upcoming={data?.outstanding?.upcoming}
              />

              {/* 4. O'rtacha kv/m² narxi */}
              <KpiCard
                title={t('boss.dashboardV2.avgPrice')}
                icon='bi bi-bar-chart-line'
                color='#3b82f6'
                sum={data?.avg_price_m2?.sum || 0}
                valute={data?.avg_price_m2?.valute || 0}
                footer={t('boss.dashboardV2.avgPriceHint')}
              />

              {/* 5. Qolgan uylar potensiali */}
              <KpiCard
                title={t('boss.dashboardV2.remainingPotential')}
                icon='bi bi-graph-up-arrow'
                color='#a855f7'
                sum={data?.remaining_potential?.sum || 0}
                valute={data?.remaining_potential?.valute || 0}
                footer={
                  <>
                    {data?.remaining_potential?.homes_count || 0}{' '}
                    {t('boss.dashboardV2.remainingHomes')} ·{' '}
                    <NumericFormat
                      value={data?.remaining_potential?.square || 0}
                      displayType='text'
                      thousandSeparator=' '
                      decimalScale={2}
                    />{' '}
                    m²
                  </>
                }
              />
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default memo(Dashboard);
